"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Menu, X } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";

// Recursive submenu component for desktop navigation
function DesktopSubmenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="grid min-w-[200px] gap-1 p-2 bg-popover text-popover-foreground rounded-md border border-border shadow-md">
      {items.map((item, i) => <li key={item.id} data-index={i} className="relative group/sub">
          {item.children && item.children.length > 0 ? <div className="flex flex-col">
              <div className="flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground rounded-sm hover:bg-muted/80 transition-colors">
                <span>{item.label}</span>
                <ChevronRight className="size-3.5 text-muted-foreground" />
              </div>
              <div className="pl-3 border-l border-border mt-1 ml-2">
                <DesktopSubmenu items={item.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="block select-none rounded-sm px-3 py-2 text-sm text-foreground hover:bg-muted/80 hover:text-foreground transition-colors font-medium">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Desktop Menu Consumer
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-20 bg-muted/60 animate-pulse rounded-sm" />
        <div className="h-8 w-24 bg-muted/60 animate-pulse rounded-sm" />
        <div className="h-8 w-20 bg-muted/60 animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-muted/70 hover:text-foreground focus:bg-muted/70 focus:text-foreground font-medium text-sm tracking-normal h-9 px-3.5 rounded-sm">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <DesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-sm px-3.5 text-sm font-medium text-foreground hover:bg-muted/70 hover:text-foreground transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive Mobile Submenu List
function MobileSubmenuList({
  items,
  depth = 1
}: {
  items: MenuItem[];
  depth?: number;
}) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleSubmenu = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <ul className={`flex flex-col gap-1 ${depth > 1 ? "pl-4 border-l border-border mt-1" : ""}`}>
      {items.map((item, i) => {
      const hasKids = item.children && item.children.length > 0;
      const isOpen = !!openItems[item.id];
      return <li key={item.id} data-index={i} className="flex flex-col">
            {hasKids ? <div>
                <Button type="button" variant="ghost" onClick={() => toggleSubmenu(item.id)} className="flex h-auto w-full items-center justify-between p-0 py-2 text-left text-base font-medium text-foreground hover:bg-transparent hover:text-primary transition-colors" aria-expanded={isOpen}>
                  <span>{item.label}</span>
                  <ChevronDown className={`size-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </Button>
                {isOpen && item.children && <div className="pb-2">
                    <MobileSubmenuList items={item.children} depth={depth + 1} />
                  </div>}
              </div> : <DrawerClose asChild>
                <Link to={item.href || "#"} className="py-2 text-base font-medium text-foreground hover:text-primary transition-colors block">
                  {item.label}
                </Link>
              </DrawerClose>}
          </li>;
    })}
    </ul>;
}

// Mobile Menu Consumer
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-6 w-32 bg-muted/60 animate-pulse rounded-sm" />
        <div className="h-6 w-40 bg-muted/60 animate-pulse rounded-sm" />
        <div className="h-6 w-28 bg-muted/60 animate-pulse rounded-sm" />
      </div>;
  }
  return <nav className="flex flex-col py-4">
      <MobileSubmenuList items={menuItems} />
    </nav>;
}
export default function Header() {
  const prefersReduced = useReducedMotion();
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll state to add subtle depth to header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;

        // Handle /# (scroll to top)
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }

        // Extract hash from href
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-background/95 backdrop-blur-md border-border shadow-xs" : "bg-background border-border/80"}`}>
      <motion.header initial={prefersReduced ? {
      opacity: 1
    } : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      ease: "easeOut"
    }} className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 h-18 sm:h-20 flex items-center justify-between gap-4">
        {/* Brand Logo */}
        <div className="flex items-center shrink-0">
          <Link to="/" className="inline-flex items-center focus-visible:outline-2 focus-visible:outline-ring rounded-sm">
            <WvcLogo className="h-7 w-auto text-primary" />
          </Link>
        </div>

        {/* Center Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {/* Right CTA Action & Mobile Toggle */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden sm:flex items-center">
            <Button asChild variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/5 rounded-sm font-medium px-4 h-9 transition-colors text-xs uppercase tracking-wider">
              <Link to="/NewsletterPage">Subscribe</Link>
            </Button>
          </div>

          {/* Mobile Navigation Drawer */}
          <div className="lg:hidden flex items-center">
            <SafeHydrate><Drawer direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="size-9 text-foreground hover:bg-muted/70 rounded-sm" aria-label="Open navigation menu">
                  <Menu className="size-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="p-6 bg-background text-foreground flex flex-col justify-between h-full max-w-sm">
                <div>
                  <DrawerHeader className="p-0 pb-6 border-b border-border flex items-center justify-between text-left">
                    <DrawerTitle className="text-left font-serif text-lg font-semibold tracking-tight text-foreground">
                      Navigation
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-foreground rounded-sm" aria-label="Close navigation menu">
                        <X className="size-4" />
                      </Button>
                    </DrawerClose>
                  </DrawerHeader>

                  <div className="mt-4 overflow-y-auto max-h-[calc(100vh-220px)] pr-1">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>
                </div>

                <div className="pt-6 mt-auto border-t border-border flex flex-col gap-3">
                  <DrawerClose asChild>
                    <Button asChild variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium py-3 text-sm">
                      <Link to="/NewsletterPage">Subscribe to Reports</Link>
                    </Button>
                  </DrawerClose>
                  <p className="text-xs text-muted-foreground text-center">
                    Peer-reviewed market insights and B2B research.
                  </p>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </motion.header>
    </section>;
}